import { Divider, Grid, Menu } from '@mui/material';
import { RoutalAvatar, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import UserIcon from '../../../../assets/img/project-users-icon.svg';
import VehicleIcon from '../../../../assets/img/project-vehicle-icon.svg';
import * as routing from '../../../../constants/Routing';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveOrganizationResources } from '../../../../hooks/organization/useRetrieveOrganizationResources';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveFlatProjects } from '../../../../hooks/project/useRetrieveFlatProjects';
import { useAsUser } from '../../../../hooks/session/useAsUser';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Text from '../../../../react-components/Text';
import { getUploadAsBase64 } from '../../../../services/requests';
import { SwitchProjectDialog } from './SwitchProjectDialog';

const CustomMenuItem = styled.div`
  min-width: 256px;
  height: 30px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ProjectContainer = styled.div.attrs((props: { asUser: boolean }) => ({
  asUser: props.asUser,
}))`
  width: 38px;
  height: 38px;
  margin: 13px 3px;
  background: ${(props) => (!props.asUser ? `transparent` : `tomato`)};
  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;
  border-radius: 4px;
`;
const StyledMenu = styled(Menu)({
  '.MuiList-root': { padding: 0 },
});

export const SideBarProject = () => {
  const [projectAnchorEl, setProjectAnchorEl] = useState<(EventTarget & HTMLDivElement) | null>(null);
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projects } = useRetrieveFlatProjects({ user });
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { asUser } = useAsUser();
  const navigate = useNavigate();

  const [switchProjectDialogOpen, setSwitchProjectDialogOpen] = useState(false);

  const activeUsers = useMemo(() => {
    return activeProject ? projects?.find((p) => p.id === activeProject.id)?.users ?? 0 : 0;
  }, [activeProject, projects]);

  const activeVehicles = useMemo(() => {
    return activeProject ? organizationResources?.getProjectResources(activeProject.id)?.vehicles ?? 0 : 0;
  }, [activeProject, organizationResources]);

  const projectActions = useMemo(() => {
    if (!user || !projects) return [];

    const actions = [];

    if (activeProject) {
      actions.push({
        id: `active-project`,
        name: `active-project`,
        label: (
          <CustomMenuItem
            style={{
              height: `auto`,
              justifyContent: `flex-start`,
              padding: `12px 12px`,
              flexDirection: `column`,
              alignContent: `center`,
              alignItems: `flex-start`,
              cursor: `default`,
              backgroundColor: `transparent`,
              width: `280px`,
            }}
          >
            <Text style={{ fontSize: `14px`, color: RoutalPalette.primary80 }}>{activeProject?.label}</Text>
            <Text style={{ fontSize: `12px`, color: RoutalPalette.secondary20 }}>
              {activeProject?.location?.label ?? intl.formatMessage({ id: `projects.undefined_location` })}
            </Text>
            <VSpacer small />
            <Grid style={{ display: `flex`, alignItems: `center` }}>
              <img src={UserIcon} alt="users" style={{ width: `16px`, height: `16px`, marginRight: `4px` }} />
              <Text
                style={{ fontSize: `12px`, color: RoutalPalette.secondary20, marginRight: `8px` }}
                intlId="projects.users"
                intlValues={{ num: activeUsers }}
              />

              <img src={VehicleIcon} alt="vehicles" style={{ width: `16px`, height: `16px`, marginRight: `4px` }} />
              <Text
                style={{ fontSize: `12px`, color: RoutalPalette.secondary20 }}
                intlId="projects.vehicles"
                intlValues={{
                  num: activeVehicles,
                }}
              />
            </Grid>
          </CustomMenuItem>
        ),
      });
    } else {
      actions.push({
        label: (
          <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `4px 12px` }}>
            <Text style={{ cursor: `pointer` }} intlId="projects.not_assigned_projects.leftbar" />
          </CustomMenuItem>
        ),
      });
    }

    actions.push(
      { isDivider: true },
      {
        id: `change-project`,
        onClick: () => {
          setSwitchProjectDialogOpen(true);
        },
        label: (
          <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `4px 12px` }}>
            <Text style={{ cursor: `pointer` }} intlId="projects.change_project" />
          </CustomMenuItem>
        ),
      },
      {
        id: `manage-projects`,
        name: `manage-projects`,
        onClick: () => {
          navigate(routing.SETTINGS.ORGANIZATION.PROJECTS);
        },
        label: (
          <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `4px 12px` }}>
            <Text style={{ cursor: `pointer` }} intlId="projects.manage_all" />
          </CustomMenuItem>
        ),
      },
      { isDivider: true }
    );

    return actions;
  }, [user, projects, activeProject, activeUsers, activeVehicles, navigate]);

  const handleProjectItemAction = (action: any) => {
    setProjectAnchorEl(null);
    action.onClick && action.onClick();
  };

  return (
    <>
      <RoutalTooltip id="current_project" title={activeProject?.label || `Default project`} placement="right">
        <ProjectContainer onClick={(e) => setProjectAnchorEl(e.currentTarget)} asUser={!!asUser}>
          <RoutalAvatar
            url={activeProject?.avatar_url}
            getUploadAsBase64={getUploadAsBase64}
            label={activeProject?.label}
            containerStyles={{ width: `28px`, height: `28px`, borderRadius: `25%` }}
          />
        </ProjectContainer>
      </RoutalTooltip>
      {user && projectActions.length > 0 && (
        <StyledMenu
          id="project-actions"
          anchorEl={projectAnchorEl}
          open={Boolean(projectAnchorEl)}
          anchorOrigin={{ horizontal: `right`, vertical: `top` }}
          onClose={(_e) => setProjectAnchorEl(null)}
        >
          {projectActions.map((action, i) =>
            (action as any).isDivider ? (
              <Divider key={`divider${i}`} />
            ) : (
              <div key={`${action.id}-${i}`} style={{ width: `100%` }} onClick={() => handleProjectItemAction(action)}>
                {action.label}
              </div>
            )
          )}
        </StyledMenu>
      )}

      {projects && activeProject && organizationResources && (
        <SwitchProjectDialog
          open={switchProjectDialogOpen}
          projects={projects}
          activeProject={activeProject}
          organizationResources={organizationResources}
          onClose={() => setSwitchProjectDialogOpen(false)}
        />
      )}
    </>
  );
};
